import { useRef, useState } from 'react';

/**
 * React hook for managing cancellable Gemini API requests with progress tracking
 * @returns {Object} Hook utilities for request management
 */
export function useGeminiRequest() {
  const abortControllerRef = useRef(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStage, setProcessingStage] = useState('');
  const [processingProgress, setProcessingProgress] = useState(0);
  const [error, setError] = useState('');

  const startRequest = async (requestFunction, progressStages = null) => {
    // Create new abort controller
    abortControllerRef.current = new AbortController();
    setIsProcessing(true);
    setProcessingProgress(0);
    setProcessingStage('Initializing request...');
    setError('');

    try {
      // Default progress stages for resume analysis
      const defaultStages = [
        { stage: 'Understanding your resume...', progress: 15 },
        { stage: 'Extracting skills and experience...', progress: 35 },
        { stage: 'Analyzing content quality...', progress: 55 },
        { stage: 'Finding job matches...', progress: 75 },
        { stage: 'Generating recommendations...', progress: 90 },
        { stage: 'Finalizing analysis...', progress: 95 }
      ];

      const stages = progressStages || defaultStages;

      // Start progress simulation
      let currentStageIndex = 0;
      const progressInterval = setInterval(() => {
        if (currentStageIndex < stages?.length) {
          const stage = stages?.[currentStageIndex];
          setProcessingStage(stage?.stage);
          setProcessingProgress(stage?.progress);
          currentStageIndex++;
        }
      }, 1200); // Slightly slower for resume analysis

      // Execute the actual request
      const result = await requestFunction(abortControllerRef?.current?.signal);
      
      // Clean up progress simulation
      clearInterval(progressInterval);
      setProcessingProgress(100);
      setProcessingStage('Complete!');
      
      return result;
    } catch (err) {
      setError(err?.message || 'An unexpected error occurred');
      throw err;
    } finally {
      setIsProcessing(false);
      abortControllerRef.current = null;
    }
  };

  const cancelRequest = () => {
    if (abortControllerRef?.current) {
      abortControllerRef?.current?.abort();
      setIsProcessing(false);
      setProcessingStage('Cancelled');
      setProcessingProgress(0);
      setError('Request was cancelled');
    }
  };

  const resetRequest = () => {
    setError('');
    setProcessingStage('');
    setProcessingProgress(0);
    setIsProcessing(false);
  };

  return {
    startRequest,
    cancelRequest,
    resetRequest,
    isProcessing,
    processingStage,
    processingProgress,
    error
  };
}

export default useGeminiRequest;